import { useState } from "react";
import { motion } from "framer-motion";
import { Droplet, Home as HomeIcon, Shield, Calendar, Heart, Leaf } from "lucide-react";
import { Btn, Eyebrow, MaskTitle, Reveal } from "@/components/site/Reveal";
import FeatureStrip from "@/components/site/FeatureStrip";
import { PRODUCTS } from "@/content";

const TANK_FEATURES = [
  { icon: Droplet, title: "Cleaner, healthier water", text: "Reduces chlorine and chloramine" },
  { icon: HomeIcon, title: "Whole home filtration", text: "Cleaner water throughout your home" },
  { icon: Shield, title: "Low maintenance", text: "Reliable, long lasting design" },
];

const DIFFERENCE = [
  { icon: Heart, title: "Supports a healthier lifestyle" },
  { icon: Leaf, title: "Better tasting water" },
  { icon: HomeIcon, title: "Cleaner home down to every tap" },
];

const Gallery = ({ images, alt }) => {
  const [active, setActive] = useState(0);
  return (
    <div className="relative h-full min-h-[420px] bg-[#EBEBEB] overflow-hidden">
      <motion.img
        key={images[active]}
        src={images[active]}
        alt={alt}
        initial={{ opacity: 0.4, scale: 1.04 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7 }}
        className="absolute inset-0 h-full w-full object-contain"
      />
      {images.length > 1 && (
        <div className="absolute bottom-5 left-5 flex gap-3">
          {images.map((src, i) => (
            <button key={src} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)} className={`h-16 w-16 bg-[#EBEBEB] border-2 transition-colors ${active === i ? "border-[var(--ink)]" : "border-white"}`}>
              <img src={src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Info = ({ p, cta, ctaTestId, animateTitle }) => (
  <>
    <Eyebrow>{p.brand}</Eyebrow>
    {animateTitle ? (
      <MaskTitle testId={`${p.id}-title`} lines={p.name.split(" ").reduce((acc, w) => { const last = acc[acc.length - 1]; if (last && (last + " " + w).length <= 16) acc[acc.length - 1] = last + " " + w; else acc.push(w); return acc; }, [])} className="mt-4 text-4xl sm:text-5xl" />
    ) : (
      <h2 data-testid={`${p.id}-title`} className="headline mt-4 text-4xl sm:text-5xl">{p.name}</h2>
    )}
    <p className="mt-3 text-xl font-head font-medium">{p.variant}</p>
    <p data-testid={`${p.id}-price`} className="mt-5 font-head font-bold text-2xl">{p.price}</p>
    <p className="mt-5 text-[16px] text-[var(--body)] leading-relaxed max-w-md">{p.text}</p>
    <Btn to={`/signup?product=${p.id}`} testId={ctaTestId} className="mt-8 !px-16">{cta}</Btn>
  </>
);

export default function Products() {
  const { tank, cartridge } = PRODUCTS;
  return (
    <>
      <section data-testid="product-tank-section" className="grid lg:grid-cols-2 border-b border-[var(--line)]">
        <motion.div initial={{ clipPath: "inset(0 100% 0 0)" }} animate={{ clipPath: "inset(0 0% 0 0)" }} transition={{ duration: 1.2, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}>
          <Gallery images={tank.images} alt="WS9 Series whole-home filtration tank" />
        </motion.div>
        <div className="flex flex-col justify-center px-5 sm:px-8 lg:px-14 py-14 lg:py-20">
          <Info p={tank} cta="Get a Quote" ctaTestId="tank-quote-btn" animateTitle />
        </div>
      </section>

      <FeatureStrip items={TANK_FEATURES} testId="product-feature-strip" />

      <section data-testid="product-cartridge-section" className="bg-white py-16 lg:py-20 border-b border-[var(--line)]">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative aspect-square max-w-[520px] w-full mx-auto bg-[#EBEBEB] overflow-hidden group">
            <img src={cartridge.images[0]} alt="WS9 Series GACC catalytic cartridge" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </Reveal>
          <Reveal delay={0.1}>
            <Info p={cartridge} cta="Add to Quote" ctaTestId="cartridge-quote-btn" />
            <div data-testid="replace-every-card" className="mt-10 bg-[var(--soft)] p-7 flex gap-6 max-w-md">
              <Calendar className="h-11 w-11 shrink-0" strokeWidth={1.4} />
              <div>
                <p className="eyebrow !text-[10px]">Replace every</p>
                <p className="headline text-2xl mt-1">12 months</p>
                <p className="mt-2 text-[14px] text-[var(--body)] leading-snug">Keep your system performing at its best with an annual cartridge replacement.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="difference-section" className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <Eyebrow>The Water System Bros. Difference</Eyebrow>
            <h2 data-testid="difference-title" className="headline mt-4 text-3xl sm:text-5xl">Cleaner water. Healthier homes.</h2>
          </Reveal>
        </div>
        <div className="mt-10">
          <FeatureStrip items={DIFFERENCE} testId="difference-strip" className="!border-y-0" />
        </div>
      </section>
    </>
  );
}
